import { AdminMessage, type BaseMessage } from "@sendbird/chat/message";
import { parseMessageData } from "@/lib/utils/parse-message-data";

const ACTIVITY_CUSTOM_TYPE = "ACTIVITY";
const ENTITY_SHARED_CUSTOM_TYPE = "ENTITY_SHARED";

const INBOUND_ACTIVITY_STATUSES = new Set(["sent", "completed"]);
const INBOUND_HUB_ENTITY_TYPES = new Set(["moment", "note"]);

interface ActivityLastMessageData {
  initiator?: string;
  status?: string;
}

interface HubLastMessageData {
  entity_type?: string;
  owner_id?: string;
}

function isInboundActivityMessage(message: AdminMessage): boolean {
  const data = parseMessageData<ActivityLastMessageData>(message.data);
  if (!data || !INBOUND_ACTIVITY_STATUSES.has(data.status ?? "")) {
    return false;
  }
  if (data.initiator === "mentor") {
    return false;
  }
  // Live cards omit initiator. Treat `sent` as a mentor take unless the card
  // says teen; `completed` without initiator is still a teen completion.
  if (data.status === "sent") {
    return data.initiator === "teen";
  }
  return data.initiator === "teen" || data.initiator == null;
}

function isInboundHubMessage(
  message: AdminMessage,
  currentUserId: string,
): boolean {
  const data = parseMessageData<HubLastMessageData>(message.data);
  if (!data?.owner_id) {
    return false;
  }
  return (
    INBOUND_HUB_ENTITY_TYPES.has(data.entity_type ?? "") &&
    data.owner_id !== currentUserId
  );
}

/** Activity/Hub admin cards have no sender; inspect payload instead. */
export function isInboundAdminLastMessage(
  message: BaseMessage | null | undefined,
  currentUserId: string,
): boolean {
  if (!message || !(message instanceof AdminMessage)) {
    return false;
  }
  if (message.customType === ACTIVITY_CUSTOM_TYPE) {
    return isInboundActivityMessage(message);
  }
  if (message.customType === ENTITY_SHARED_CUSTOM_TYPE) {
    return isInboundHubMessage(message, currentUserId);
  }
  return false;
}

export function isMentorResponseLastMessage(
  message: BaseMessage | null | undefined,
  currentUserId: string,
): boolean {
  if (!message) {
    return false;
  }
  if (message instanceof AdminMessage) {
    if (message.customType === ACTIVITY_CUSTOM_TYPE) {
      const data = parseMessageData<ActivityLastMessageData>(message.data);
      if (!data || data.initiator === "teen") {
        return false;
      }
      return data.initiator === "mentor" || data.status === "sent";
    }
    if (message.customType === ENTITY_SHARED_CUSTOM_TYPE) {
      const data = parseMessageData<HubLastMessageData>(message.data);
      if (!data?.owner_id) {
        return false;
      }
      return (
        INBOUND_HUB_ENTITY_TYPES.has(data.entity_type ?? "") &&
        data.owner_id === currentUserId
      );
    }
    return false;
  }
  const senderId = (message as { sender?: { userId?: string } }).sender?.userId;
  return Boolean(senderId) && senderId === currentUserId;
}
